'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    HealthPlan,
    deleteHealthPlan,
    getHealthPlans,
} from '@/lib/api/medical/health-plan-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdClose,
    MdDelete,
    MdEdit,
    MdHealthAndSafety,
    MdPhone,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const PLAN_TYPE_LABELS: Record<string, string> = {
    individual: 'Individual',
    empresarial: 'Empresarial',
    coletivo_adesao: 'Coletivo por Adesão',
}

export default function HealthPlanListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'health_plans'
    )
    const [allItems, setAllItems] = useState<HealthPlan[]>([])
    const [filteredItems, setFilteredItems] = useState<HealthPlan[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.organizationId) {
            loadData()
        }
    }, [user?.organizationId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase()
            const filtered = allItems.filter(
                item =>
                    item.name.toLowerCase().includes(term) ||
                    item.ansCode?.toLowerCase().includes(term) ||
                    item.cnpj?.toLowerCase().includes(term)
            )
            setFilteredItems(filtered)
        } else {
            setFilteredItems(allItems)
        }
    }, [searchTerm, allItems])

    const loadData = async () => {
        if (!user?.organizationId) return

        setLoading(true)
        try {
            const response = await getHealthPlans(user.organizationId)
            if (response.success) {
                setAllItems(response.result || [])
            } else {
                toast.error(response.error || 'Erro ao carregar convênios')
            }
        } catch {
            toast.error('Erro ao carregar convênios')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setItemToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!itemToDelete) return

        setDeleting(true)
        try {
            const response = await deleteHealthPlan(itemToDelete)
            if (response.success) {
                toast.success('Convênio excluído com sucesso!')
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao excluir convênio')
            }
        } catch {
            toast.error('Erro ao excluir convênio')
        } finally {
            setDeleting(false)
            setShowDeleteModal(false)
            setItemToDelete(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando convênios...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdHealthAndSafety className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Convênios
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {allItems.length}{' '}
                            {allItems.length === 1
                                ? 'convênio cadastrado'
                                : 'convênios cadastrados'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate('/registrations/convenios/new')
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">
                                Novo Convênio
                            </span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome, código ANS ou CNPJ..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdHealthAndSafety className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhum convênio encontrado'
                                : 'Nenhum convênio cadastrado'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Cadastre o primeiro convênio para começar'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/registrations/convenios/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Novo Convênio
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {filteredItems.map(item => (
                        <div
                            key={item.id}
                            className={`group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300 ${!item.active ? 'opacity-60' : ''}`}
                        >
                            <div className="flex items-center gap-4 p-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="text-base font-semibold text-base-content">
                                            {item.name}
                                        </h3>
                                        <span
                                            className={`badge badge-sm ${
                                                item.active
                                                    ? 'badge-success'
                                                    : 'badge-ghost'
                                            }`}
                                        >
                                            {item.active ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-4 text-sm">
                                        {item.ansCode && (
                                            <span className="badge badge-outline">
                                                ANS: {item.ansCode}
                                            </span>
                                        )}
                                        {item.planType && (
                                            <span className="badge badge-outline">
                                                {PLAN_TYPE_LABELS[
                                                    item.planType
                                                ] || item.planType}
                                            </span>
                                        )}
                                        {item.cnpj && (
                                            <span className="text-base-content/70">
                                                CNPJ: {item.cnpj}
                                            </span>
                                        )}
                                        {item.phone && (
                                            <div className="flex items-center gap-1 text-base-content/70">
                                                <MdPhone className="text-base" />
                                                {item.phone}
                                            </div>
                                        )}
                                        {item.email && (
                                            <span className="text-base-content/70">
                                                {item.email}
                                            </span>
                                        )}
                                        {item._count?.clients != null &&
                                            item._count.clients > 0 && (
                                                <span className="text-base-content/70">
                                                    {item._count.clients}{' '}
                                                    {item._count.clients === 1
                                                        ? 'paciente'
                                                        : 'pacientes'}
                                                </span>
                                            )}
                                    </div>
                                </div>

                                {(canUpdate || canDelete) && (
                                    <div className="flex items-center gap-1">
                                        {canUpdate && (
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        `/registrations/convenios/${item.id}`
                                                    )
                                                }
                                                className="btn btn-sm btn-ghost"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={() =>
                                                    handleDelete(item.id)
                                                }
                                                className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setItemToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir Convênio"
                message="Tem certeza que deseja excluir este convênio? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
